import { useMemo } from 'react';

import { Box, useBreakpointValue } from '@chakra-ui/react';

import { IconsNocowanie } from '@nocowanie/icons';

import { AccommodationItem } from '@app/components/molecules/accommodation-item';
import { SectionCarousel } from '@app/components/organisms/section-carousel';
import { SectionNameEnum } from '@app/enums';
import { PromotedAccommodations } from '@app/models';
import { cmsHomepageSlice, commonUiStateSlice, useAppSelector } from '@app/state';

import { FeaturedAccommodationsProps } from './featured-accommodations.props';

export const FeaturedAccommodations = ({ ...rest }: FeaturedAccommodationsProps) => {
    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);

    const featuredAccommodationsData: PromotedAccommodations = useAppSelector(
        cmsHomepageSlice.selectors.selectSectionData(SectionNameEnum.FeaturedAccommodationsSection),
    );

    const slidesCountBreakpoints = {
        base: 1,
        md: 2,
        lg: 3,
        xl: 4,
    };
    const breakpointFallback = isMobileBrowser ? 'base' : 'xl';

    const slidesPerView = useBreakpointValue(
        { ...slidesCountBreakpoints, base: 1.1, sm: 1.4 },
        {
            fallback: breakpointFallback,
        },
    );
    const slidesPerGroup = useBreakpointValue(slidesCountBreakpoints, {
        fallback: breakpointFallback,
    });

    const slidesElements = useMemo(
        () =>
            featuredAccommodationsData?.sections.map(section => ({
                name: section.label,
                slides:
                    section.accommodations
                        ?.filter(accommodation => accommodation.cheapestRate != null)
                        .map(accommodation => ({
                            id: accommodation.id,
                            body: (
                                <AccommodationItem
                                    {...accommodation}
                                    data-testid="featured-accommodation-item"
                                />
                            ),
                        })) || [],
            })),
        [featuredAccommodationsData],
    );

    if (!featuredAccommodationsData) return;

    const filters = featuredAccommodationsData?.sections.map(section => {
        const IconComponent = section.icon?.icons ? IconsNocowanie[section.icon.icons] : null;

        return {
            name: section.label,
            icon: (
                <>
                    {IconComponent ? (
                        <IconComponent size="sm" data-testid="featured-accommodation-icon" />
                    ) : null}
                </>
            ),
        };
    });

    const slideStyles = {
        '& swiper-container': {
            display: 'flex',
            gap: 'gutterWidthDesktop',
        },
        '& swiper-slide': {
            flexShrink: 0,
            width: {
                xl: 'calc(25% - 18px) !important', // On xl screens we have 4 slides, and we need to remove the gap between them
            },
        },
    };

    return (
        <Box data-testid={'featured-accommodations-wrapper'} css={slideStyles} {...rest}>
            <SectionCarousel
                title={featuredAccommodationsData?.headline?.headline || ''}
                subtitle={featuredAccommodationsData?.headline?.subheadline}
                filters={filters}
                slideElements={slidesElements}
                sliderParams={{
                    spaceBetween: 24,
                    slidesPerView,
                    slidesPerGroup,
                }}
                sliderOverflow={'hidden'}
                isEdgeToEdgeOnMobile={true}
            />
        </Box>
    );
};
